import React, { useState, useEffect, FC } from 'react';
import L from 'leaflet';

import { LatLngLiteral, LatLngTuple } from 'leaflet';
import { CoKitchenPolygons, CoKitchenType } from 'utils/types';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import PageHeaderText from 'components/base/page-header-text';
import Map from 'components/map';
import PolygonModal, { PolygonModalType } from 'components/polygon-modal';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import PolygonCard from 'components/polygon-card';
import { DeleteIcon, EditAltIcon } from 'cokitchen-icons';
import { PolygonItemType } from 'components/polygon-card/polygon-item';
import useDashboardContext from 'contexts/dashboard.context';
import RightSidebar from 'components/right-sidebar';
import DeleteModal from 'components/base/delete-modal';

// show marker on map
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow
});

L.Marker.prototype.options.icon = DefaultIcon;

type ActionType = 'create' | 'edit' | 'none';
type MapProps = {
  cokitchen?: CoKitchenType;
  setCords?: (cords: LatLngLiteral[]) => void;
  positions?: LatLngLiteral[] | LatLngTuple[];
  type?: ActionType;
  onPolygonClick?: (polygon: CoKitchenPolygons) => void;
  withMarker?: boolean;
};

const CoKitchenPolygon: FC<MapProps> = () => {
  const { activeCokitchen, setRightSection } = useDashboardContext();
  const [showPolygonModal, setShowPolygonModal] = useState(false);
  const [polygon, setPolygon] = useState<LatLngTuple[]>();
  const [polygons, setPolygons] = useState([] as CoKitchenPolygons[]);
  const [polygonModalType, setPolygonModalType] = useState<PolygonModalType>('edit');
  const [loading, setLoading] = useState(false);
  const [showDeletePolygonModal, setShowDeletePolygonModal] = useState(false);

  const { cokitchenPolygons, setCokitchenPolygons, activePolygon, setActivePolygon } =
    useDashboardContext();

  useEffect(() => {
    setRightSection(
      <RightSidebar list={[]}>
        <PolygonMenu />
      </RightSidebar>
    );

    return () => {
      setRightSection(null);
    };
  }, [activeCokitchen, polygons]);

  useEffect(() => {
    setPolygon(activePolygon?.polygon as LatLngTuple[]);
  }, [activePolygon]);

  const togglePolygonModal = () => {
    setShowPolygonModal(!showPolygonModal);
  };
  const createPolygon = async (payload: any) => {
    return await apiService.post(`marketing/cokitchen-polygon`, { data: payload });
  };
  const handleDeletePolygon = async () => {
    try {
      const res: any = await apiService.delete(`marketing/cokitchen-polygon/${activePolygon?.id}`);
      if (res.data) {
        toast(<ToastMessage type='success' message='Polygon deleted successfully' />);
        setShowDeletePolygonModal(false);
        fetchData();
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    }
  };

  const editPolygon = () => {
    togglePolygonModal();
    setPolygonModalType('edit');
  };

  const POLYGON_MENU: PolygonItemType[] = [
    {
      name: 'Edit Polygon',
      icon: <EditAltIcon />,
      onClick: editPolygon
    },
    {
      name: 'Delete Polygon',
      icon: <DeleteIcon className='text-red-5' />,
      onClick: () => setShowDeletePolygonModal(true),
      className: 'text-red-5'
    }
  ];

  useEffect(() => {
    if (cokitchenPolygons?.length > 0) {
      createAllPolygon();
    }
  }, [cokitchenPolygons]);

  const fetchData = async () => {
    try {
      const res: any = await apiService.get(`internal/cokitchen/polygons/${activeCokitchen.id}`, {
        isInternal: true
      });
      setPolygons(res.data.data);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const createAllPolygon = async () => {
    setLoading(true);
    for (const item of cokitchenPolygons) {
      const payload = {
        cokitchen_id: activeCokitchen?.id,
        ...item
      };

      try {
        const res = await createPolygon(payload);
        if (res?.data) {
          setPolygons([...polygons, res.data.data]);
          toast(<ToastMessage type='success' message={'Polygons have been added successfully'} />);
          setCokitchenPolygons([]);
          setPolygonModalType('edit');
        }
      } catch (err: any) {
        toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
      }
    }

    setLoading(false);
  };

  const PolygonMenu = () => {
    return (
      <PolygonCard list={POLYGON_MENU} setActivePolygon={setActivePolygon} polygons={polygons} />
    );
  };

  return (
    <div className='map min-h-screen'>
      <div className='mb-6 flex justify-between items-center yo '>
        <PageHeaderText title='Manage Polygons' />
        <button
          className='border border-[#E7E8E9] shadow text-[#5C5F64] w-[86px] h-[40px] py-2 rounded-[8px]'
          onClick={() => {
            togglePolygonModal();
            setPolygonModalType('create');
          }}>
          Create
        </button>
      </div>
      <Map type={'edit'} positions={polygon} />

      <PolygonModal
        show={showPolygonModal}
        onClose={() => {
          togglePolygonModal();
        }}
        positions={polygon as LatLngTuple[]}
        data={{
          name: activePolygon?.name ?? '',
          delivery_fee: activePolygon?.delivery_fee ?? '',
          delivery_service_charge: activePolygon?.delivery_service_charge ?? '',
          cokitchen_polygon_id: activePolygon?.id ?? '',
          delivery: activePolygon?.delivery ?? false,
          pickup: activePolygon?.pickup ?? false
        }}
        type={polygonModalType}
        onEditComplete={fetchData}
      />

      <DeleteModal
        show={showDeletePolygonModal}
        onClose={() => setShowDeletePolygonModal(false)}
        handleDelete={handleDeletePolygon}
        description='This action cannot be undone'
        title={`Are you sure you want to delete this polygon`}
        loading={loading}
      />
    </div>
  );
};

export default CoKitchenPolygon;
